import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";

// Shared setup for every TomSelect dropdown.
//
// TomSelect builds its own markup next to the <select> it enhances, and Turbo
// has no idea that markup is generated: it goes into the page cache, and it
// travels along with elements marked turbo-permanent. So a dropdown often
// reconnects to a <select> that already carries TomSelect's classes, sitting
// beside wrappers left over from earlier renders. Those leftovers are dead
// copies -- no instance behind them -- and clicking one does nothing, which is
// how the filters on the manage pages "stop working after a while".
//
// Every connect therefore starts from a clean <select>: tear down whatever is
// there, generated markup included, then build the one instance we own.
export default class extends Controller {
  connect() {
    this.teardown();
    this.tomSelect = new TomSelect(this.selectElement, this.tomSelectOptions);
  }

  disconnect() {
    this.teardown();
  }

  // The <select> to enhance. Subclasses that sit on a wrapper point this at a
  // target instead.
  get selectElement() {
    return this.element;
  }

  // Subclasses override this with their own TomSelect settings.
  get tomSelectOptions() {
    return {};
  }

  teardown() {
    const select = this.selectElement;
    const instance = select.tomselect;

    if (instance) {
      // destroy() puts back the options the <select> had when TomSelect was
      // built, which would drop anything selected or created since. Hold on to
      // both so the dropdown keeps showing what is actually filtered -- the
      // selection separately, because TomSelect records it as a property that
      // innerHTML does not carry.
      const options = select.innerHTML;
      const selected = Array.from(select.selectedOptions, (o) => o.value);

      instance.destroy();

      select.innerHTML = options;
      Array.from(select.options).forEach((option) => {
        option.selected = selected.includes(option.value);
      });
    }

    let sibling = select.nextElementSibling;
    while (sibling?.classList.contains("ts-wrapper")) {
      const next = sibling.nextElementSibling;
      sibling.remove();
      sibling = next;
    }

    select.classList.remove("tomselected", "ts-hidden-accessible");
  }
}
